import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';

/**
 * Keeps exactly one desktop nav mega menu open. Opening a new menu clears any
 * pending close timer and immediately replaces the previous open id so panels
 * cannot stack when the pointer moves quickly between triggers.
 */
export default function useNavMegaMenuGroup() {
  const [openId, setOpenId] = useState(null);
  const closeTimer = useRef(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const openMenu = useCallback((id) => {
    clearCloseTimer();
    setOpenId(id);
  }, [clearCloseTimer]);

  const scheduleClose = useCallback((id) => {
    if (Platform.OS !== 'web') return;
    clearCloseTimer();
    closeTimer.current = setTimeout(() => {
      setOpenId((current) => (current === id ? null : current));
      closeTimer.current = null;
    }, 400);
  }, [clearCloseTimer]);

  const closeMenu = useCallback((id) => {
    clearCloseTimer();
    setOpenId((current) => (current === id ? null : current));
  }, [clearCloseTimer]);

  const closeAll = useCallback(() => {
    clearCloseTimer();
    setOpenId(null);
  }, [clearCloseTimer]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  return {
    openId,
    isOpen: (id) => openId === id,
    openMenu,
    scheduleClose,
    closeMenu,
    closeAll,
  };
}
